import { SortDirection } from '@payaca/types/listViewTypes';
import { useReducer } from 'react';

import {
  AutomationActionType,
  AutomationEntityType,
  AutomationsInput,
  AutomationsSortBy,
  AutomationTriggerEvent,
  PaginationInput,
} from '@/gql/graphql';

export const AUTOMATIONS_PAGE_SIZE = 20;

export type AutomationsSortableField = 'title' | 'updatedAt';

const sortFieldToGql: Record<AutomationsSortableField, AutomationsSortBy> = {
  title: 'NAME',
  updatedAt: 'UPDATED_AT',
};

type Filters = {
  entityTypes: AutomationEntityType[];
  triggerEvents: AutomationTriggerEvent[];
  actionTypes: AutomationActionType[];
};

type ListState = Filters & {
  search: string;
  sortBy: AutomationsSortableField;
  sortDirection: SortDirection;
  pageNumber: number;
};

type ListAction =
  | { type: 'search'; search: string }
  | { type: 'filter'; filters: Partial<Filters> }
  | {
      type: 'sort';
      sortBy: AutomationsSortableField;
      sortDirection: SortDirection;
    }
  | { type: 'page'; pageNumber: number };

const INITIAL_STATE: ListState = {
  search: '',
  entityTypes: [],
  triggerEvents: [],
  actionTypes: [],
  sortBy: 'updatedAt',
  sortDirection: SortDirection.DESCENDING,
  pageNumber: 1,
};

const listReducer = (state: ListState, action: ListAction): ListState => {
  switch (action.type) {
    case 'search':
      // Search input fires on both blur and debounce, often with an unchanged term
      if (action.search === state.search) return state;
      return { ...state, search: action.search, pageNumber: 1 };
    case 'filter':
      return { ...state, ...action.filters, pageNumber: 1 };
    case 'sort':
      return {
        ...state,
        sortBy: action.sortBy,
        sortDirection: action.sortDirection,
        pageNumber: 1,
      };
    case 'page':
      return { ...state, pageNumber: action.pageNumber };
  }
};

export const useAutomationsListState = () => {
  const [state, dispatch] = useReducer(listReducer, INITIAL_STATE);
  const {
    search,
    entityTypes,
    triggerEvents,
    actionTypes,
    sortBy,
    sortDirection,
    pageNumber,
  } = state;

  const input: AutomationsInput = {
    search: search || undefined,
    entityTypes: entityTypes.length ? entityTypes : undefined,
    triggerEvents: triggerEvents.length ? triggerEvents : undefined,
    actionTypes: actionTypes.length ? actionTypes : undefined,
    sortBy: sortFieldToGql[sortBy],
    sortDirection: sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
  };

  const pagination: PaginationInput = {
    limit: AUTOMATIONS_PAGE_SIZE,
    offset: (pageNumber - 1) * AUTOMATIONS_PAGE_SIZE,
  };

  const isFiltered =
    !!search ||
    entityTypes.length > 0 ||
    triggerEvents.length > 0 ||
    actionTypes.length > 0;

  return {
    ...state,
    input,
    pagination,
    isFiltered,
    applySearch: (value: string) => dispatch({ type: 'search', search: value }),
    setFilters: (filters: Partial<Filters>) =>
      dispatch({ type: 'filter', filters }),
    setSort: (field: AutomationsSortableField, direction: SortDirection) =>
      dispatch({ type: 'sort', sortBy: field, sortDirection: direction }),
    setPageNumber: (value: number) =>
      dispatch({ type: 'page', pageNumber: value }),
  };
};
